export class Gallery {
    /**
     * Initializes the Gallery class.
     */
    constructor() {
        this.biggerPictureInstance = null;
        this.imageCache = new Map(); // Cache for image dimensions
        this.init();
    }

    /**
     * Initializes BiggerPicture support.
     */
    init() {
        // Check whether BiggerPicture is available
        if (typeof BiggerPicture === 'undefined') {
            DCMSDebugLog('BiggerPicture library not found', 2, 'Gallery');
            return;
        }

        this.initBiggerPicture();
        DCMSDebugLog('initialized', 3, 'Gallery');
    }

    /**
     * Initializes the BiggerPicture instance and event listeners.
     */
    initBiggerPicture() {
        this.biggerPictureInstance = BiggerPicture({
            target: document.body,
        });

        // Event listener for the gallery - we fetch the current elements dynamically
        $(document).on('click', '.dcmsGallery a, a.dcmsGallery, [data-dcms-gallery] a, a[data-dcms-gallery]', 
            (e) => this.openBPGallery(e)
        );

        // Event listener for the popup (single photos)
        $(document).on('click', '.dcmsPopup a, a.dcmsPopup, [data-dcms-popup] a, a[data-dcms-popup]', 
            (e) => this.openBPPopup(e)
        );

        DCMSDebugLog('BiggerPicture initialized with gallery and popup support', 3, 'Gallery');
    }

    /**
     * Gets the proper image URL from an element.
     * @param {HTMLElement} element - Element to check
     * @returns {string|null} - Image URL or null
     */
    getImageUrl(element) {
        // Check data-img as the first option
        if (element.hasAttribute('data-img')) {
            return element.getAttribute('data-img');
        }
        
        // Check href, but only if it's not "#"
        if (element.href && element.href !== window.location.href + '#' && element.href !== '#') {
            return element.href;
        }
        
        // Check src for img elements
        if (element.src) {
            return element.src;
        }
        
        // Check whether the element contains an img
        const imgElement = element.querySelector('img');
        if (imgElement && imgElement.src) {
            return imgElement.src;
        }
        
        return null;
    }

    /**
     * Checks whether the image is already loaded in the DOM.
     * @param {string} imageUrl - Image URL
     * @returns {HTMLImageElement|null} - img element if found, otherwise null
     */
    findLoadedImage(imageUrl) {
        // Check all img elements on the page
        const images = document.querySelectorAll('img');
        for (const img of images) {
            if (img.src === imageUrl && img.complete && img.naturalWidth > 0) {
                return img;
            }
        }
        return null;
    }

    /**
     * Gets image dimensions from cache or auto-detects them.
     * @param {string} imageUrl - Image URL
     * @returns {Promise<{width: number, height: number}>} - Image dimensions
     */
    async getImageDimensions(imageUrl) {
        // Check whether the URL is valid
        if (!imageUrl || imageUrl === '#' || !this.isImageUrl(imageUrl)) {
            DCMSDebugLog(`Invalid image URL: ${imageUrl}`, 2, 'Gallery');
            return { width: 1920, height: 1080 };
        }

        // Check the cache
        if (this.imageCache.has(imageUrl)) {
            DCMSDebugLog(`Using cached dimensions for: ${imageUrl}`, 3, 'Gallery');
            return this.imageCache.get(imageUrl);
        }

        // Check whether the image already exists in the DOM
        const loadedImage = this.findLoadedImage(imageUrl);
        if (loadedImage) {
            const dimensions = {
                width: loadedImage.naturalWidth,
                height: loadedImage.naturalHeight
            };
            this.imageCache.set(imageUrl, dimensions);
            DCMSDebugLog(`Using loaded image dimensions: ${imageUrl} - ${dimensions.width}x${dimensions.height}`, 3, 'Gallery');
            return dimensions;
        }

        // Load the image in the background to detect its dimensions
        return new Promise((resolve) => {
            const img = new Image();
            
            img.onload = () => {
                const dimensions = {
                    width: img.naturalWidth,
                    height: img.naturalHeight
                };
                
                // Save to cache
                this.imageCache.set(imageUrl, dimensions);
                DCMSDebugLog(`Image dimensions detected: ${imageUrl} - ${dimensions.width}x${dimensions.height}`, 3, 'Gallery');
                resolve(dimensions);
            };
            
            img.onerror = () => {
                DCMSDebugLog(`Failed to load image for dimension detection: ${imageUrl}`, 2, 'Gallery');
                // Return default dimensions on error
                const defaultDimensions = { width: 1920, height: 1080 };
                this.imageCache.set(imageUrl, defaultDimensions);
                resolve(defaultDimensions);
            };
            
            img.src = imageUrl;
        });
    }

    /**
     * Prepares gallery elements with automatic dimension detection.
     * @param {Array|NodeList} elements - Elements to prepare
     * @param {boolean} detectDimensions - Whether to auto-detect dimensions (default true)
     * @returns {Promise<Array>} - Prepared elements with dimensions
     */
    async prepareGalleryItems(elements, detectDimensions = true) {
        const preparedItems = [];
        
        for (const element of elements) {
            const imageUrl = this.getImageUrl(element);
            let width = null;
            let height = null;
            
            // Check whether the element already has dimensions provided
            if (element.hasAttribute('data-width') && element.hasAttribute('data-height')) {
                width = parseInt(element.getAttribute('data-width'));
                height = parseInt(element.getAttribute('data-height'));
                DCMSDebugLog(`Using provided dimensions for ${imageUrl}: ${width}x${height}`, 3, 'Gallery');
            } 
            // Check whether it's an img element with natural dimensions
            else if (element.tagName === 'IMG' && element.naturalWidth && element.naturalHeight) {
                width = element.naturalWidth;
                height = element.naturalHeight;
                DCMSDebugLog(`Using natural dimensions for ${imageUrl}: ${width}x${height}`, 3, 'Gallery');
            }
            // Check whether the element contains an img with dimensions
            else {
                const imgElement = element.querySelector('img');
                if (imgElement && imgElement.naturalWidth && imgElement.naturalHeight) {
                    width = imgElement.naturalWidth;
                    height = imgElement.naturalHeight;
                    DCMSDebugLog(`Using child img dimensions for ${imageUrl}: ${width}x${height}`, 3, 'Gallery');
                }
                // Automatic dimension detection
                else if (detectDimensions && imageUrl) {
                    try {
                        const dimensions = await this.getImageDimensions(imageUrl);
                        width = dimensions.width;
                        height = dimensions.height;
                    } catch (error) {
                        DCMSDebugLog(`Error getting dimensions for ${imageUrl}: ${error}`, 2, 'Gallery');
                        width = 1920;
                        height = 1080;
                    }
                }
            }

            // Add dimensions to the element if it doesn't have them and we have valid values
            if (width && height && imageUrl) {
                if (!element.hasAttribute('data-width')) {
                    element.setAttribute('data-width', width);
                }
                if (!element.hasAttribute('data-height')) {
                    element.setAttribute('data-height', height);
                }
                
                // Set the proper image URL if we're using data-img
                if (element.hasAttribute('data-img') && element.href === '#') {
                    // BiggerPicture needs href, so we temporarily copy data-img into href
                    element.setAttribute('data-original-href', element.href);
                    element.href = imageUrl;
                }
            }

            preparedItems.push(element);
        }
        
        return preparedItems;
    }

    /**
     * Restores the original href for elements that were modified.
     * @param {Array} elements - Elements to restore
     */
    restoreOriginalHrefs(elements) {
        elements.forEach(element => {
            if (element.hasAttribute('data-original-href')) {
                element.href = element.getAttribute('data-original-href');
                element.removeAttribute('data-original-href');
            }
        });
    }

    /**
     * Opens the BiggerPicture gallery.
     * @param {Event} e - Click event
     */
    async openBPGallery(e) {
        e.preventDefault();
        
        const clickedElement = e.currentTarget;
        let galleryItems;
        
        // Fetch the current gallery elements dynamically
        const allGalleryItems = document.querySelectorAll(
            '.dcmsGallery a, a.dcmsGallery, [data-dcms-gallery] a, a[data-dcms-gallery]'
        );
        
        DCMSDebugLog(`Opening gallery for element: ${this.getImageUrl(clickedElement) || clickedElement.href}`, 3, 'Gallery');
        DCMSDebugLog(`Found ${allGalleryItems.length} total gallery items on page`, 3, 'Gallery');
        
        // PRIORITY 1: Check data-dcms-gallery (attributes)
        const galleryContainer = clickedElement.closest('[data-dcms-gallery]');
        const directGalleryName = clickedElement.getAttribute('data-dcms-gallery');
        
        if (galleryContainer || directGalleryName) {
            // Get the gallery name
            const galleryName = galleryContainer ? 
                              galleryContainer.getAttribute('data-dcms-gallery') : 
                              directGalleryName;
            
            DCMSDebugLog(`Gallery identified by data-dcms-gallery: "${galleryName}"`, 3, 'Gallery');
            
            // Filter elements belonging to the same gallery by name
            galleryItems = Array.from(allGalleryItems).filter(item => {
                const itemContainer = item.closest('[data-dcms-gallery]');
                const itemGalleryName = itemContainer ? 
                                      itemContainer.getAttribute('data-dcms-gallery') : 
                                      item.getAttribute('data-dcms-gallery');
                return itemGalleryName === galleryName;
            });
        }
        // PRIORITY 2: Check data-gallery-name on an element with the dcmsGallery class
        else if (clickedElement.classList.contains('dcmsGallery') && clickedElement.hasAttribute('data-gallery-name')) {
            const galleryName = clickedElement.getAttribute('data-gallery-name');
            
            DCMSDebugLog(`Gallery identified by data-gallery-name: "${galleryName}"`, 3, 'Gallery');
            
            // Filter elements with the same gallery name
            galleryItems = Array.from(allGalleryItems).filter(item => {
                const itemGalleryName = item.getAttribute('data-gallery-name');
                const itemHasClass = item.classList.contains('dcmsGallery');
                
                DCMSDebugLog(`Checking item: ${this.getImageUrl(item)}, galleryName: "${itemGalleryName}", hasClass: ${itemHasClass}`, 3, 'Gallery');
                
                return itemHasClass && itemGalleryName === galleryName;
            });
        }
        // PRIORITY 3: Check the .dcmsGallery container
        else {
            const galleryContainer = clickedElement.closest('.dcmsGallery');
            
            if (galleryContainer) {
                DCMSDebugLog('Gallery identified by .dcmsGallery container', 3, 'Gallery');
                
                // Filter only elements from the same gallery container
                galleryItems = Array.from(allGalleryItems).filter(item => {
                    return item.closest('.dcmsGallery') === galleryContainer;
                });
            } else if (clickedElement.classList.contains('dcmsGallery')) {
                // If the link itself has the dcmsGallery class without data-gallery-name
                DCMSDebugLog('Gallery identified by direct .dcmsGallery class (no name)', 3, 'Gallery');
                
                galleryItems = Array.from(allGalleryItems).filter(item => {
                    return item.classList.contains('dcmsGallery') && !item.hasAttribute('data-gallery-name');
                });
            } else {
                // Fallback - use all elements
                DCMSDebugLog('Using fallback - all gallery items', 2, 'Gallery');
                galleryItems = allGalleryItems;
            }
        }

        DCMSDebugLog(`Preparing gallery with ${galleryItems.length} items`, 3, 'Gallery');

        // Prepare elements with automatic dimension detection
        try {
            const preparedItems = await this.prepareGalleryItems(galleryItems, true);
            
            DCMSDebugLog(`Opening gallery with ${preparedItems.length} prepared items`, 3, 'Gallery');

            this.biggerPictureInstance.open({
                items: preparedItems,
                el: clickedElement,
                onClosed: () => {
                    // Restore the original href after closing
                    this.restoreOriginalHrefs(preparedItems);
                }
            });
        } catch (error) {
            DCMSDebugLog(`Error preparing gallery items: ${error}`, 1, 'Gallery');
            // Fallback - open without preparing the dimensions
            this.biggerPictureInstance.open({
                items: galleryItems,
                el: clickedElement,
            });
        }
    }

    /**
     * Opens a single photo as a popup.
     * @param {Event} e - Click event
     */
    async openBPPopup(e) {
        e.preventDefault();
        
        const clickedElement = e.currentTarget;
        
        DCMSDebugLog(`Opening popup for element: ${this.getImageUrl(clickedElement) || clickedElement.href}`, 3, 'Gallery');
        
        try {
            // Prepare the element with automatic dimension detection
            const preparedItems = await this.prepareGalleryItems([clickedElement], true);
            
            this.biggerPictureInstance.open({
                items: preparedItems, // Use the prepared element
                el: clickedElement,
                noCounter: true, // Hides the photo counter
                noClose: false,   // Keeps the close button
                onClosed: () => {
                    // Restore the original href after closing
                    this.restoreOriginalHrefs(preparedItems);
                }
            });
        } catch (error) {
            DCMSDebugLog(`Error preparing popup item: ${error}`, 1, 'Gallery');
            // Fallback - open without preparing the dimensions
            this.biggerPictureInstance.open({
                items: [clickedElement],
                el: clickedElement,
                noCounter: true,
                noClose: false
            });
        }
    }

    /**
     * Preloads image dimensions for better performance.
     * This function is available for manual calls, but is not run automatically.
     * @param {string|Array} imageUrls - Image URL or an array of URLs
     */
    async preloadImageDimensions(imageUrls) {
        const urls = Array.isArray(imageUrls) ? imageUrls : [imageUrls];
        
        DCMSDebugLog(`Preloading dimensions for ${urls.length} images`, 3, 'Gallery');
        
        const promises = urls.map(url => this.getImageDimensions(url));
        
        try {
            await Promise.all(promises);
            DCMSDebugLog(`Successfully preloaded dimensions for ${urls.length} images`, 3, 'Gallery');
        } catch (error) {
            DCMSDebugLog(`Error preloading some image dimensions: ${error}`, 2, 'Gallery');
        }
    }

    /**
     * Manually preloads the dimensions of all gallery images on the page.
     * This function is NOT called automatically - call it manually if needed.
     */
    async preloadAllGalleryImages() {
        const galleryLinks = document.querySelectorAll(
            '.dcmsGallery a, a.dcmsGallery, [data-dcms-gallery] a, a[data-dcms-gallery], .dcmsPopup a, a.dcmsPopup, [data-dcms-popup] a, a[data-dcms-popup]'
        );
        
        const imageUrls = Array.from(galleryLinks)
            .map(link => this.getImageUrl(link))
            .filter(url => url && this.isImageUrl(url));
        
        if (imageUrls.length > 0) {
            DCMSDebugLog(`Found ${imageUrls.length} gallery images to preload`, 3, 'Gallery');
            await this.preloadImageDimensions(imageUrls);
        }
    }

    /**
     * Checks whether a URL points to an image.
     * @param {string} url - URL to check
     * @returns {boolean} - True if it's an image
     */
    isImageUrl(url) {
        if (!url || url === '#') return false;
        const imageExtensions = /\.(jpg|jpeg|jfif|png|gif|bmp|webp|svg)(\?.*)?$/i;
        return imageExtensions.test(url);
    }

    /**
     * Clears the image dimensions cache.
     */
    clearImageCache() {
        this.imageCache.clear();
        DCMSDebugLog('Image dimensions cache cleared', 3, 'Gallery');
    }

    /**
     * Closes the currently open gallery or popup.
     */
    close() {
        if (this.biggerPictureInstance) {
            this.biggerPictureInstance.close();
            DCMSDebugLog('Gallery/popup closed', 3, 'Gallery');
        }
    }

    /**
     * Checks whether BiggerPicture is currently open.
     * @returns {boolean} - True if open
     */
    isOpen() {
        // BiggerPicture doesn't have a direct method to check its state
        // We can check whether a .bp-wrap element exists in the DOM
        return document.querySelector('.bp-wrap') !== null;
    }

    /**
     * Programmatically opens the gallery for a given element.
     * @param {string|HTMLElement} element - Selector or DOM element
     * @param {Object} options - Additional options for BiggerPicture
     */
    async openElement(element, options = {}) {
        const targetElement = typeof element === 'string' ? document.querySelector(element) : element;
        
        if (!targetElement) {
            DCMSDebugLog(`Element not found: ${element}`, 2, 'Gallery');
            return;
        }

        DCMSDebugLog(`Programmatically opening element: ${this.getImageUrl(targetElement) || targetElement.href}`, 3, 'Gallery');

        try {
            // Prepare the element with automatic dimension detection
            const preparedItems = await this.prepareGalleryItems([targetElement], true);

            const defaultOptions = {
                items: preparedItems,
                el: targetElement,
                onClosed: () => {
                    this.restoreOriginalHrefs(preparedItems);
                }
            };

            this.biggerPictureInstance.open({
                ...defaultOptions,
                ...options
            });
        } catch (error) {
            DCMSDebugLog(`Error preparing element: ${error}`, 1, 'Gallery');
            // Fallback
            this.biggerPictureInstance.open({
                items: [targetElement],
                el: targetElement,
                ...options
            });
        }
    }

    /**
     * Refreshes the list of gallery elements (useful after dynamically added content).
     */
    refreshGalleryItems() {
        DCMSDebugLog('Refreshing gallery items', 3, 'Gallery');
        
        // After refreshing the elements, we don't need to do anything special
        // because we use event delegation via $(document).on()
        // which automatically handles newly added elements
        // and we fetch the current elements dynamically in openBPGallery()
        
        const galleryCount = document.querySelectorAll('.dcmsGallery a, a.dcmsGallery, [data-dcms-gallery] a, a[data-dcms-gallery]').length;
        const popupCount = document.querySelectorAll('.dcmsPopup a, a.dcmsPopup, [data-dcms-popup] a, a[data-dcms-popup]').length;
        
        DCMSDebugLog(`Gallery items refreshed. Gallery links: ${galleryCount}, Popup links: ${popupCount}`, 3, 'Gallery');

        // NOTE: preloading has been removed from automatic refresh
        // Use manually: await window.APP.galleryInstance.preloadAllGalleryImages();
    }

    /**
     * Removes all event listeners and clears the instance.
     */
    destroy() {
        if (this.biggerPictureInstance) {
            // BiggerPicture doesn't have a destroy method, but we can remove the event listeners
            $(document).off('click', '.dcmsGallery a, a.dcmsGallery, [data-dcms-gallery] a, a[data-dcms-gallery]');
            $(document).off('click', '.dcmsPopup a, a.dcmsPopup, [data-dcms-popup] a, a[data-dcms-popup]');
            
            this.biggerPictureInstance = null;
            this.clearImageCache();
            DCMSDebugLog('Gallery destroyed', 3, 'Gallery');
        }
    }
}